import React from 'react'
import {useTranslation} from 'react-i18next'

import type {InputComponentProps} from './types'
import {
  getPlanRecommendation,
  getPlanRecommendationUpdates,
  isCentricHealthRecommendation,
  PLAN_FIELDS,
  PLAN_RECOMMENDATIONS,
  type PlanRecommendation,
} from '../planRecommendation'

import {Checkbox} from '~/components/general/checkbox/Checkbox'
import {Container} from '~/components/general/container/Container'
import {Input} from '~/components/general/input/Input'
import {Option, Select} from '~/components/general/select/Select'
import {Typography} from '~/components/general/typography/Typography'
import {StyledDashboardInnerCardTableCell} from '~/components/facility/GeneralDataCard.styled'

type PlanRecommendationInputProps = InputComponentProps

export const PlanRecommendationInput = ({
  title,
  currentData,
  onUpdate,
}: PlanRecommendationInputProps) => {
  const {t} = useTranslation<string>()
  const recommendation = getPlanRecommendation(currentData)
  const useCentricHealth = isCentricHealthRecommendation(currentData)
  const gpDetails =
    currentData.find((item) => item.name === PLAN_FIELDS.gpVisitDetails)
      ?.value ?? ''
  const emergencyDepartmentDetails =
    currentData.find(
      (item) => item.name === PLAN_FIELDS.emergencyDepartmentDetails,
    )?.value ?? ''

  const handleRecommendationChange = (
    selectedRecommendation: PlanRecommendation,
  ) => {
    onUpdate(
      getPlanRecommendationUpdates(
        selectedRecommendation,
        selectedRecommendation === PLAN_FIELDS.gpVisit && useCentricHealth,
      ),
    )
  }

  const handleCentricHealthChange = () => {
    onUpdate([
      {
        name: PLAN_FIELDS.gpVisit,
        value: useCentricHealth ? 'Own' : 'Centric Health',
      },
    ])
  }

  const handleDetailsChange = (name: string, value: string) => {
    onUpdate([{name, value}])
  }

  return (
    <StyledDashboardInnerCardTableCell colSpan={2}>
      <Container direction="column" spacing={1}>
        <Typography weight="medium" size="sm" inline>
          {title}
        </Typography>
        <Select
          placeholder={t('Select')}
          value={t(recommendation)}
          size="small">
          {PLAN_RECOMMENDATIONS.map((option) => (
            <Option
              onClick={() => handleRecommendationChange(option)}
              key={option}>
              <Typography weight="medium">{t(option)}</Typography>
            </Option>
          ))}
        </Select>

        {recommendation === PLAN_FIELDS.gpVisit && (
          <>
            <Checkbox
              name="centric-health-recommendation"
              label={t('Centric Health')}
              checked={useCentricHealth}
              onChange={handleCentricHealthChange}
            />
            <Container direction="column">
              <Typography weight="medium" size="sm" inline>
                {t(PLAN_FIELDS.gpVisitDetails)}
              </Typography>
              <Input
                size="small"
                placeholder={t('Enter value')}
                value={gpDetails}
                onChange={(event) =>
                  handleDetailsChange(
                    PLAN_FIELDS.gpVisitDetails,
                    event.target.value,
                  )
                }
              />
            </Container>
          </>
        )}

        {recommendation === PLAN_FIELDS.emergencyDepartment && (
          <Container direction="column">
            <Typography weight="medium" size="sm" inline>
              {t(PLAN_FIELDS.emergencyDepartmentDetails)}
            </Typography>
            <Input
              size="small"
              placeholder={t('Enter value')}
              value={emergencyDepartmentDetails}
              onChange={(event) =>
                handleDetailsChange(
                  PLAN_FIELDS.emergencyDepartmentDetails,
                  event.target.value,
                )
              }
            />
          </Container>
        )}
      </Container>
    </StyledDashboardInnerCardTableCell>
  )
}
